import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { getDepartmentsActionData, getDepartmentsAction, addActionComments, editActionComments } from "../../store/actions/departmentActions";
import { editAction, editActionProgressLabel } from "../../store/actions/riskActions";
import { LuClock, LuCircleAlert, LuGripVertical } from "react-icons/lu";
import ActionOverlay from "../../components/Action/ActionOverlay";

import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';



const CLICK_DIST = 4;   // px
const CLICK_TIME = 500; // ms
const DRAG_DELAY_MS = 150;

// Keep the file lean—remove unused imports to silence lints

const STAGES = ["Open", "In Progress", "Problem", "Completed"];

// Map any source (DB/UI/legacy) → canonical UI stage
const normalizeProgress = (p) => {
    const v = (p ?? "").toString().trim().toLowerCase();
    if (v === "in progress" || v === "in_progress" || v === "doing") return "In Progress";
    if (v === "blocked" || v === "hold" || v === "problem") return "Problem";
    if (v === "done" || v === "completed" || v === "complete") return "Completed";
    if (v === "not started" || v === "not_started" || v === "open") return "Open";
    return "Open";
};

const deriveLocal = (a) => ({
    ...a,
    __id: getActionId(a), // your existing helper
    __risk: a?.risk_id ?? a?.RiskID ?? a?.riskId,
    __stage: normalizeProgress(a.progressLabel ?? a.progress ?? a.Progress ?? a.Status),
});

const stageToDb = (stage) => {
    const v = (stage ?? "").toString().trim().toLowerCase();
    if (v === "in progress") return "In progress";
    if (v === "blocked") return "Problem";
    if (v === "done") return "Completed";
    return "Not started";
};




// DB enum → UI
const dbToStage = (progress) => {
    const v = (progress ?? "").toString().trim().toLowerCase();
    if (v === "in progress") return "In Progress";
    if (v === "problem") return "Blocked";
    if (v === "completed") return "Done";
    return "Open"; // "Not started"
};
// Helper to read IDs safely from various payloads
const getActionId = (a) => a?.action_id ?? a?.iid ?? a?.id;
const getRiskId = (a) => a?.risk_id ?? a?.RiskID ?? a?.riskId;

const KanBanComponent = (props) => {
    const {
        actions,                       // from state.department.actions
        activeDepartmentId,            // passed from parent
        getDepartmentsActionFN,        // thunk: fetch actions
        getDepartmentsActionDataFN,    // thunk: fetch stats (optional)
        editActionFN,                  // thunk: (risk_id, action_id, payload)
        editActionProgressLabelFN,
    } = props;



    const [activeDepartment, setActiveDepartment] = useState(activeDepartmentId ? activeDepartmentId : null);
    const [risk_id, setRiskId] = useState(null);
    const [isActionOpen, setIsActionOpen] = useState(false);
    const [action_id, setActionId] = useState(null);
    const [local, setLocal] = useState([]);

    useEffect(() => {


        if (activeDepartmentId) {
            setActiveDepartment(activeDepartmentId)

        }
    }, [activeDepartmentId]);

    const pick = (...vals) => vals.find(v => v != null && v !== "") ?? undefined;

    const getTitle = (a) =>
        pick(a?.title, a?.action_title, a?.ActionTitle, a?.name, a?.task_title, a?.TaskTitle)
        || `Action #${a?.__id ?? a?.action_id ?? a?.iid ?? a?.id}`;

    const getOwners = (a) => {
        const raw =
            a?.owners ?? a?.assignees ?? a?.Owners ?? a?.Assignees ?? a?.owner_list ?? a?.assigned_users ?? [];
        // normalize to [{name, avatar}]
        return (Array.isArray(raw) ? raw : []).map(u => {
            if (typeof u === "string") return { name: u, avatar: null };
            return {
                name: pick(u?.name, u?.full_name, u?.label, u?.email, u?.username),
                avatar: pick(u?.avatar, u?.photo_url, u?.image, u?.img, u?.picture),
            };
        });
    };

    const getDue = (a) => pick(a?.EndDate, a?.end_date, a?.due_date, a?.DueDate);
    const getRiskTitle = (a) => pick(a?.risk_title, a?.RiskTitle);
    const getPriority = (a) => pick(a?.priority, a?.Priority);
    const getActionId = (a) => a?.action_id ?? a?.iid ?? a?.id;
    const looksLikeAction = (a) => {
        if (!a) return false;
        // hard excludes
        if (a.type === "project") return false;                  // risks in your Gantt builder
        if ("risk_title" in a && !("action_id" in a)) return false;
        // positives
        if (a.action_id != null) return true;
        if (a.iid != null && (a.Progress != null || a.progress != null)) return true;
        if (a.kind === "action" || a.Source === "Actions" || a.table_name === "3457_Actions") return true;
        return false;
    };

    // 1) Fetch when department changes / first mount
    useEffect(() => {
        if (activeDepartmentId) {
            getDepartmentsActionFN(activeDepartmentId);
            // optionally also pull stats for this page:
            getDepartmentsActionDataFN?.(activeDepartmentId);
        }
    }, [activeDepartmentId, getDepartmentsActionFN, getDepartmentsActionDataFN]);

    const [draggingId, setDraggingId] = useState(null);


    const patchLocalCard = (updated) => {
        const u = deriveLocal(updated);
        setLocal(prev => {
            const idx = prev.findIndex(x => x.__id === u.__id || x.iid === u.iid);
            if (idx === -1) return [u, ...prev];          // if not found, prepend
            const next = prev.slice();
            next[idx] = { ...prev[idx], ...u };
            return next;
        });
    };

    useEffect(() => {
        const list = Array.isArray(actions) ? actions : [];

        setLocal(list.map(a => ({
            ...a,
            __id: getActionId(a),
            __risk: a?.risk_id ?? a?.RiskID ?? a?.riskId,
            __stage: normalizeProgress(
                a.progressLabel ?? a.progress ?? a.Progress ?? a.Status
            ),
        })));
    }, [actions]);

    // 3) Group into columns
    const columns = useMemo(() => {
        const by = Object.fromEntries(STAGES.map((s) => [s, []]));
        for (const a of local) by[a.__stage]?.push(a);
        return by;
    }, [local]);

    // 4) Drag & drop
    const onDragStart = (id) => setDraggingId(id);
    const onDragEnd = () => setDraggingId(null);

    const handleDrop = useCallback(async (stageRaw) => {
        if (!draggingId) return;

        const stage = normalizeProgress(stageRaw); // ensure canonical label

        // derive riskId + optimistic update from freshest state
        let riskId = null;
        setLocal(prev => {
            const dragged = prev.find(x => x.__id === draggingId);
            riskId = dragged?.__risk || null;
            return prev.map(a => (a.__id === draggingId ? { ...a, __stage: stage } : a));
        });

        try {
            if (riskId && draggingId) {
                await editActionProgressLabelFN(draggingId, stage);
            }
            if (activeDepartmentId) {
                await getDepartmentsActionDataFN(activeDepartmentId);
            }
        } catch (e) {
            // rollback to the prior, normalized value from any of the known fields
            setLocal(prev =>
                prev.map(a =>
                    a.__id === draggingId
                        ? {
                            ...a,
                            __stage: normalizeProgress(a.progress ?? a.Progress ?? a.Status ?? a.__stage),
                        }
                        : a
                )
            );
            console.error("Failed to move card:", e);
        } finally {
            setDraggingId(null);
        }
    }, [draggingId, editActionProgressLabelFN, getDepartmentsActionDataFN, activeDepartmentId]);


    const closeAction = async (data, action_id) => {
        try {
            // 1) save to server
            const saved = await updateControlServerData(data, action_id); // ideally returns the updated action
            // 2) optimistic local patch from server response (if you have it)
            if (saved?.action || saved?.data) {
                patchLocalCard(saved.action ?? saved.data);
            }

            // 3) optional: hard refresh -> normalize into local
            const fresh = await getDepartmentsActionDataFN(activeDepartment);
            if (Array.isArray(fresh)) {
                setLocal(fresh.map(deriveLocal)); // update cards
            }

            // if you also maintain a separate Gantt "tasks":
            // setTasks(normalizeTasks(Array.isArray(fresh) ? fresh : []));

        } catch (e) {
            console.error("Close action failed:", e);
            // soft fallback: do nothing; the overlay will still close
        } finally {
            setIsActionOpen(false);
        }
    };

    const openAction = (action) => {
        //console.log("action", action);
        if (!action.iid)
            return
        setActionId(action.iid);
        setRiskId(action.risk_id);
        setIsActionOpen(true);
    };


    const updateControlServerData = async (data, action_id) => {
        // adjust dates if you really need +1 day
        const updatedData = { ...data };
        if (updatedData.StartDate) {
            const d = new Date(updatedData.StartDate);
            d.setDate(d.getDate() + 1);
            updatedData.StartDate = d.toISOString().split("T")[0];
        }
        if (updatedData.EndDate) {
            const d = new Date(updatedData.EndDate);
            d.setDate(d.getDate() + 1);
            updatedData.EndDate = d.toISOString().split("T")[0];
        }


        try {
            // IMPORTANT: await the thunk
            const saved = await editActionFN(risk_id, action_id, updatedData);
            return saved; // bubble up if you want
        } finally {

        }
    };

    function isBehind(t) {
        try {
            if (t?.styles?.backgroundColor === "#F07167") return true; // backend hint

            const start = new Date(t.start ?? t.start_date ?? t.StartDate);
            const end = new Date(t.end ?? t.end_date ?? t.due ?? t.DueDate);
            const now = new Date();

            if (isNaN(start) || isNaN(end) || end <= start) return false;

            const total = end - start;
            const elapsed = now - start;
            const fracRaw = elapsed / total;
            const frac = Math.max(0, Math.min(1, fracRaw)); // clamp

            const rawPct = t.progress ?? t.Progress ?? t.percent ?? t.pct;
            const pct = Number(String(rawPct ?? "").replace("%", ""));
            if (!Number.isFinite(pct)) return false;

            const label = (t.progressLabel ?? t.status ?? t.Status ?? "").trim();
            const completed = ["Done", "Completed", "Closed"].includes(label);

            return pct < frac * 100 && !completed;
        } catch {
            return false;
        }
    }


    const normalizeForProgress = (a) => {
        const pick = (...keys) => keys.find((k) => a?.[k] != null);
        const toNum = (v) => {
            if (v == null) return null;
            const n = Number(String(v).replace("%", ""));
            return Number.isFinite(n) ? n : null;
        };

        const startKey = pick("start", "start_date", "StartDate");
        const endKey = pick("end", "end_date", "due", "DueDate");
        const progKey = pick("progress", "Progress", "percent", "pct");
        const labelKey = pick("progressLabel", "status", "Status");

        return {
            start: a[startKey],
            end: a[endKey],
            progress: toNum(a[progKey]),
            progressLabel: a[labelKey],
            styles: a.styles, // keep backend hint if present
        };
    };


    // 5) UI
    function setCleanDragImage(e, sourceEl) {
        const ghost = sourceEl.cloneNode(true);
        Object.assign(ghost.style, {
            position: "fixed",
            top: "-10000px",
            left: "-10000px",
            pointerEvents: "none",
            opacity: "1",
            transform: "none",
            filter: "none",
            backdropFilter: "none",
        });
        ghost.classList?.remove?.("hover:-translate-y-0.5");
        document.body.appendChild(ghost);
        try {
            e.dataTransfer.setDragImage(ghost, 0, 0);
        } catch { }
        setTimeout(() => ghost.remove(), 0);
    }


    const Card = ({ a, draggingId, onDragStart, onDragEnd, onOpen }) => {
        const owners = getOwners(a);
        const due = getDue(a);
        const riskTitle = getRiskTitle(a);
        const priority = getPriority(a);

        // ---- OPTIONAL: fallback if you don't already have these helpers ----
        const label = a.progressLabel || a.status || "";
        const isCompleted = (lbl) => ["Done", "Completed", "Closed"].includes((lbl || "").trim());
        const endDate = a.end || a.end_date || a.due || null;
        const today = new Date();
        const overdue = !!endDate && new Date(endDate) < today && !isCompleted(label);
        // if you have a richer "behind" calc, use it; this is a light heuristic:
        const norm = normalizeForProgress(a);

        const behind = isBehind(norm);
        // ---- END fallback helpers ----

        // click/drag suppression (unchanged)
        const suppressUntilRef = useRef(0);
        const didDragRef = useRef(false);

        const handleDragStart = (e) => {
            didDragRef.current = true;
            suppressUntilRef.current = Date.now() + 500;
            onDragStart?.(a.__id);
            e.dataTransfer.setData("text/plain", String(a.__id));
            e.dataTransfer.effectAllowed = "move";
        };

        const handleDragEnd = (e) => {
            suppressUntilRef.current = Date.now() + 500;
            didDragRef.current = false;
            onDragEnd?.(e);
        };

        const handleClickCapture = (e) => {
            if (Date.now() < suppressUntilRef.current) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        const handleClick = () => {
            if (Date.now() >= suppressUntilRef.current) onOpen?.(a);
        };

        return (
            <div
                draggable
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onClickCapture={handleClickCapture}
                onClick={handleClick}
                className={`group relative rounded-xl border border-gray-200 bg-white backdrop-blur-sm p-3 shadow-sm hover:shadow-md transition-all duration-200 cursor-grab active:cursor-grabbing select-none ${draggingId === a.__id ? "opacity-60 scale-[0.98]" : "hover:-translate-y-0.5"
                    }`}
            >
                {/* Priority chip (top-right) */}
                {priority && (
                    <div className="absolute bottom-2 right-3">
                        {(() => {
                            const label = priority.trim();
                            const base =
                                "text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-medium";
                            switch (label) {
                                case "Critical":
                                    return (
                                        <span
                                            className={`${base} bg-red-50 border-red-200 text-red-700 font-semibold`}
                                        >
                                            Critical
                                        </span>
                                    );
                                case "High":
                                    return (
                                        <span
                                            className={`${base} bg-red-50 border-red-200 text-red-700 font-semibold`}
                                        >
                                            High
                                        </span>
                                    );
                                case "Medium":
                                    return (
                                        <span
                                            className={`${base} bg-orange-50 border-orange-200 text-orange-600 font-semibold`}
                                        >
                                            Medium
                                        </span>
                                    );
                                case "Low":
                                    return (
                                        <span
                                            className={`${base} bg-yellow-50 border-yellow-200 text-yellow-600 font-medium`}
                                        >
                                            Low
                                        </span>
                                    );
                                default:
                                    return null;
                            }
                        })()}
                    </div>
                )}

                {/* Title + status chips */}
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-[#000] group-hover:text-[#0E4A62]">
                            {getTitle(a)}
                        </span>

                        {behind && (
                            <span className="ml-0.5 text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
                                <LuCircleAlert className="w-3 h-3" /> behind
                            </span>
                        )}

                        {overdue && (
                            <span className="ml-0.5 text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                <LuClock className="w-3 h-3" /> overdue
                            </span>
                        )}
                    </div>
                </div>

                {/* Subtle subtitle (risk name) */}
                {riskTitle && (
                    <div className="mt-0.5 text-xs text-[#3b3b3b] line-clamp-1">{riskTitle}</div>
                )}

                {/* Meta row */}
                <div className="mt-3 flex items-center justify-between">
                    <AvatarGroup users={owners} />
                    <div className="flex items-center gap-1">
                        {due && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full border border-gray-200 text-[#000] bg-gray-50">
                                Due {due}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );

    };


    const AvatarGroup = ({ users = [], max = 3 }) => {
        const shown = users.slice(0, max);
        const overflow = users.length - shown.length;
        return (
            <div className="flex -space-x-2">
                {shown.map((u, i) => (
                    <div key={i} className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-white bg-gray-200 overflow-hidden">
                        {u.avatar ? (
                            <img src={u.avatar} alt={u.name || "user"} className="h-full w-full object-cover" />
                        ) : (
                            <span className="text-[10px] text-gray-600 font-medium">
                                {(u.name || "?").slice(0, 2).toUpperCase()}
                            </span>
                        )}
                    </div>
                ))}
                {overflow > 0 && (
                    <div className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-white bg-gray-100">
                        <span className="text-[10px] text-gray-600 font-medium">+{overflow}</span>
                    </div>
                )}
            </div>
        );
    };


    const Column = ({ stage, items }) => {
        const colorMap = {
            Open: "#3B82F6",          // blue
            "In Progress": "#FACC15", // yellow
            Problem: "#EF4444",       // red
            Completed: "#22C55E",     // green,
        };
        const dotColor = colorMap[stage] || "#9CA3AF"; // gray fallback

        return (
            <div
                className="
        flex flex-col w-[320px] rounded-2xl border border-gray-300 shadow-sm
        overflow-hidden
        max-h-[calc(100vh-140px)]   /* cap to viewport; tweak 140 if needed */
        min-h-[70vh]
      "
                style={{ backgroundColor: "#F6F8FA" }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(stage)}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200/70 bg-white/70 rounded-t-2xl">
                    <div className="flex items-center gap-2 font-semibold text-sm text-gray-800">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: dotColor }} />
                        {stage}
                    </div>
                    <div className="px-2 py-0.5 rounded-full text-xs bg-gray-100 border border-gray-200 text-gray-700">
                        {items.length}
                    </div>
                </div>

                {/* Cards (scrolls) */}
                <div
                    className="
          flex-1 min-h-0 overflow-y-auto space-y-3 p-3
          overscroll-contain scroll-pb-24 pr-4
        "
                >
                    {items.map((a) => (
                        <Card
                            key={a.__id}
                            a={a}
                            draggingId={draggingId}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            onOpen={openAction}
                        />
                    ))}

                    {items.length === 0 && (
                        <div className="text-xs text-gray-400 italic">No items</div>
                    )}

                    {/* bottom spacer so the last card can scroll fully above the edge */}
                    <div aria-hidden className="h-12 shrink-0" />
                </div>
            </div>
        );
    };
    // Empty-state guard so you “see something” even before data arrives
    const total = local.length;

return (
  <div className="w-full min-h-0">
    {total === 0 ? (
      <div className="text-sm text-gray-500 p-4">Loading actions…</div>
    ) : (
      <div className="min-h-0">
        {/* Left-aligned, consistent spacing; wraps on small, no-wrap on md+ */}
        <div className="flex gap-4 flex-wrap md:flex-nowrap items-start min-w-0 w-full">
          {STAGES.map((s) => (
            <div key={s} className="flex-1 min-w-[260px]">
              <Column stage={s} items={columns[s] || []} />
            </div>
          ))}
        </div>
      </div>
    )}

    <Popup
      open={isActionOpen}
      contentStyle={{
        overflow: "auto",
        maxHeight: "80vh",
        width: "900px",
        marginTop: "14vh",
      }}
    >
      <ActionOverlay
        closeAction={closeAction}
        risk_id={risk_id}
        action_id={action_id}
      />
    </Popup>
  </div>
);






};

/* ===== Redux wiring at bottom (as you prefer) ===== */

const mapStateToProps = (state) => {
    console.log("actions_stats ", state.department.actions_data)
    return {
        departments: state.department.departments,
        actions: state.department.actions_data,
        profile: state.profile.profile,
        actions_stats: state.department.actions_data,
    }
};

const mapDispatchToProps = (dispatch) => ({
    getDepartmentsActionFN: (department_id) => dispatch(getDepartmentsAction(department_id)),
    getDepartmentsActionDataFN: (department_id) => dispatch(getDepartmentsActionData(department_id)),
    addActionCommentsFN: (action_id, comments, department_id) =>
        dispatch(addActionComments(action_id, comments, department_id)),
    editActionCommentsFN: (comment_iid, comments, department_id) =>
        dispatch(editActionComments(comment_iid, comments, department_id)),
    editActionFN: (risk_id, action_id, action) => dispatch(editAction(risk_id, action_id, action)),
    editActionProgressLabelFN: (draggingId, stage) => dispatch(editActionProgressLabel(draggingId, stage)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(KanBanComponent);
